import { splitParentSummary } from './splitParentSummary.js'
import ChampionshipArtworkCell from '../ChampionshipArtworkCell.jsx'
import SchoolTeamCoverage from '../../bowl/SchoolTeamCoverage.jsx'
import { useMemo, useState, useCallback, Fragment } from 'react'
import {
  groupByStage, PURSUIT_STAGES, makeComparator, formatDate, formatCountdownFor,
  daysToEvent, sportMeta, contentionMeta, ownerName, windowCounts, inRollingWindow,
  WINDOW_STEPS, stageTally,
} from '../../../lib/championships.js'
import { SelectAllTh, SelectCell } from './RowSelect.jsx'
import SubmitLink from './SubmitLink.jsx'
import ChampionCell from './ChampionCell.jsx'
import TradeChip from '../../trades/TradeChip.jsx'
import HuntBar from './HuntBar.jsx'
import DateStamp from './DateStamp.jsx'
import usePhone from '../../../lib/usePhone.js'
import styles from './pursuit.module.css'

/**
 * THE BOARD — the full championship universe under a REAL rolling 7/30/60/90
 * look-back / look-forward lens.
 *
 * Two hard rules, both of them fixes to what shipped:
 *
 *  1. NOTHING IS HIDDEN. Every stage group renders, including the out-of-window
 *     Earlier / Later tails and the undated records. Long groups paginate with
 *     an explicit "showing X of N" receipt and a Show-all control — a page is a
 *     page, never a silent cap. The footer reconciles the rendered row count
 *     against the total so the two can be compared at a glance.
 *
 *  2. THE WINDOWS ARE ROLLING AND KEYED OFF THE EFFECTIVE DATE. "Just
 *     Concluded" means within the last 7 days, not "any past event", and
 *     "Imminent" means the next 7. The lens pills are built from WINDOW_STEPS
 *     and scope the board to a cumulative window (Last 30 contains Last 7);
 *     "All records" is the default so the whole universe is browsable.
 *
 * Rows carry the fields that drive a pursuit decision: championship + captured
 * champion (♛), sport (color-coded), level, division, event date + countdown,
 * status, incumbent, and OWNER (blank reads "Unassigned" — reps are being
 * unassigned in HubSpot, so blank is expected, never an error). Row → drawer.
 */
/**
 * The columns, built from the SPACE and from what the records actually carry.
 * The championship set is unchanged; a pursuit space swaps the dimensions that
 * mean nothing to it (sport / division / incumbent) for the ones that decide a
 * bid: PRODUCT (what the RFP is for), STATE, and the surfaced date — and its
 * date column is named for the deadline it counts down to.
 */
const columnsFor = (noun, dateLabel, { pursuit, product, state, open }) => [
  { key: 'name', label: noun, sortable: true, cls: 'colName' },
  ...(pursuit ? [] : [{ key: 'sport', label: 'Sport', sortable: true }]),
  ...(product ? [{ key: 'product', label: 'Product', sortable: true }] : []),
  { key: 'level', label: 'Level', sortable: true },
  ...(pursuit ? [] : [{ key: 'segment', label: 'Division', sortable: true }]),
  ...(state ? [{ key: 'state', label: 'State', sortable: true }] : []),
  ...(open ? [{ key: 'openDate', label: 'Surfaced', sortable: true }] : []),
  { key: 'date', label: dateLabel, sortable: true },
  { key: 'status', label: 'Status', sortable: false },
  /* THE CHAMPION — a column, not a detail nested inside the name.
   *
   * Brian: "we need a column that actually shows the winner, instead of being
   * nested under the championship."
   *
   * This table had Sport, Level, Division, Date, Status, Incumbent and Owner
   * but no column for WHO WON — the one fact the whole system exists to
   * produce. It carries its own trust verdict, its problems and its source
   * URL inline (see ChampionCell), so a rep scanning 427 rows can see which
   * winners are believable without opening anything.
   *
   * Absent in pursuit spaces for the same reason `incumbent` is: a Greek
   * chapter or a corporation does not win a championship, so a champion
   * column there is a category error, not an empty cell. */
  ...(pursuit ? [] : [{ key: 'champion', label: 'Champion', sortable: true, cls: 'colChampion' }]),
  ...(pursuit ? [] : [{ key: 'artwork', label: 'Artwork', sortable: false, cls: 'colArtwork' }]),
  ...(pursuit ? [] : [{ key: 'incumbent', label: 'Incumbent', sortable: true }]),
  { key: 'owner', label: 'Assigned rep', sortable: true },
  { key: 'cta', label: 'Actions', sortable: false },
]

/* How many rows each group renders per page. This is a PAGE SIZE, not a cap:
 * every group states its full count and expands to all of it on demand.
 *
 * ⭐ TWO SIZES, AND THE PHONE ONE IS THE WHOLE FIX. Brian: *"The 1485 need to
 * get done."*
 *
 * Measured 2026-10-17 on the live board at 390px: 10,885 interactive elements, 1,485 of
 * them under 44px, and 1,447 of those 1,485 are two controls repeated once per
 * row — the trust chip (982, one per row) and the champion source link (465).
 * The previous lane read that correctly and said so: raising a 20-22px inline
 * chip to 44px would add ~88px per row, about 86,000px of extra phone scroll,
 * to satisfy a metric. It then named the real defect and left it as a product
 * decision: ten stage groups × 100 rows is 982 rows on a 390px screen.
 *
 * 982 rows is not a tap-target problem. Nobody scrolls 982 rows on a phone, so
 * every control below the first screenful is unreachable in practice whatever
 * size it is — and the count of sub-44 targets is a near-exact multiple of the
 * row count, so the row count IS the number. Paging the phone to 12 rows a
 * group fixes the usability problem and the metric together, and it does it by
 * rendering less rather than by growing everything.
 *
 * ⚠️ NOTHING IS HIDDEN AND THAT IS LOAD-BEARING. Every group still prints its
 * own full count, "showing N of M", a "Show more", a "Show all", and the
 * footer still reconciles the stages to the whole view. A smaller page with no
 * way out would be worse than a long one; this is a smaller page with the same
 * three ways out it already had.
 *
 * 12 rather than 25: at 390px cc-mobile.css renders each row as a CARD, and a
 * card is roughly 180px tall. Twelve cards is about two thumb-flicks and leaves
 * the group bands — which is how a rep navigates this board — reachable without
 * scrolling past a wall. */
const PAGE_DESKTOP = 100
const PAGE_PHONE = 12

/* Paint a record with a champion we JUST wrote and confirmed.
 *
 * Both naming conventions are set because the cell reads the camelCase view
 * model while the trust layer reads the raw HubSpot property, and setting only
 * one produces the precise failure this board keeps hitting: a champion in one
 * cell and "unverified" in the one beside it. Trust is 'sourced' because the
 * accept path REFUSES to write without a source URL — so if we got here, there
 * is one. */
function crownedView(c, overlay) {
  const hit = overlay[String(c.id)]
  if (!hit) return c
  return {
    ...c,
    champion: hit.champion,
    current_champion: hit.champion,
    championSourceUrl: hit.sourceUrl,
    champion_source_url: hit.sourceUrl,
    championTrust: 'sourced',
    championIssues: [],
  }
}

export default function PursuitStages({
  items, now, onOpen, actions = {}, selection = null, noun = 'Championship', dateLabel = 'Event date',
}) {
  // What this universe actually carries. A pursuit space is detected from the
  // records themselves (`sourceType`), never from emptiness — a championship
  // universe can never take this branch.
  const shape = useMemo(() => ({
    pursuit: items.some((c) => c.sourceType),
    product: items.some((c) => c.product),
    state: items.some((c) => c.sourceType && c.state && c.state !== 'NATIONAL'),
    open: items.some((c) => c.openDate),
  }), [items])
  const COLUMNS = useMemo(() => columnsFor(noun, dateLabel, shape), [noun, dateLabel, shape])
  // The per-row write console only exists where the write actually exists —
  // outside the Championships space no handler is passed and the row keeps only
  // its read-only "Open ›" CTA.
  const canWrite = !!(actions.onCreateArt || actions.onSubmitWin || actions.onCampaign)
  const [sort, setSort] = useState({ field: 'date', dir: 'asc' })
  const [lens, setLens] = useState(null)            // null = all records
  const [limits, setLimits] = useState(() => ({}))  // stage key → rows rendered
  /* The page size follows the viewport, so the phone renders a phone's worth.
   * See PAGE_PHONE above for the measurement this comes from. */
  const phone = usePhone()
  const PAGE = phone ? PAGE_PHONE : PAGE_DESKTOP

  /* JUST-CROWNED OVERLAY.
   *
   * Accepting a hunt result writes to HubSpot, but `items` came from a feed of
   * 13,000+ records that is not going to re-fetch per accept. Without this the
   * row still reads "SEARCHED, NOT FOUND" directly under a champion we just
   * saved — which is the exact contradiction (a stale display arguing with a
   * fresh write) that this board has been bitten by all week.
   *
   * It is deliberately NOT a second source of truth: it holds only what the
   * server confirmed it wrote, and the next real load replaces it. */
  const [justCrowned, setJustCrowned] = useState({})
  const onAccepted = useCallback((id, { champion, sourceUrl }) => {
    setJustCrowned((m) => ({ ...m, [String(id)]: { champion, sourceUrl } }))
  }, [])

  const counts = useMemo(() => windowCounts(items, now), [items, now])

  // The lens scopes the board to a rolling window. Default is the FULL universe.
  const scoped = useMemo(() => {
    if (!lens) return items
    return items.filter((c) => inRollingWindow(c, lens.side, lens.span, now))
  }, [items, lens, now])

  const groups = useMemo(() => groupByStage(scoped, now), [scoped, now])
  const cmp = useMemo(() => makeComparator(sort.field, sort.dir, now), [sort, now])
  const sorted = useMemo(() => {
    const g = {}
    for (const s of PURSUIT_STAGES) g[s.key] = [...groups[s.key]].sort(cmp)
    return g
  }, [groups, cmp])

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' }))

  const limitFor = (key) => limits[key] ?? PAGE
  const bump = (key, n) => setLimits((s) => ({ ...s, [key]: n }))
  /* ⚠️ `limits` IS DELIBERATELY NOT RESET WHEN THE WIDTH CHANGES. A rep who
   * expanded a group and then rotated the phone has made a decision; throwing
   * it away on a resize would look like the board losing their place. The
   * default is what tracks the width, and `collapseAll` clears the overrides
   * back to that default. */
  const expandAll = () =>
    setLimits(Object.fromEntries(PURSUIT_STAGES.map((s) => [s.key, sorted[s.key].length])))
  const collapseAll = () => setLimits({})

  // Group bands span the full table — one wider when the checkbox column is on.
  const fullSpan = COLUMNS.length + (selection ? 1 : 0)

  const totalScoped = scoped.length
  const rendered = PURSUIT_STAGES.reduce((n, s) => n + Math.min(limitFor(s.key), sorted[s.key].length), 0)
  const allRendered = rendered >= totalScoped

  /* THE SUMMARY IS DERIVED FROM THE GROUPS, NOT HAND-PICKED.
   * See stageTally's header for the 203-record defect this replaces. */
  const tally = useMemo(() => stageTally(groups, totalScoped), [groups, totalScoped])

  const pill = (side, span, label, count) => {
    const on = lens && lens.side === side && lens.span === span
    return (
      <button
        key={`${side}${span}`}
        className={`${styles.lensPill} cc-lenspill ${on ? styles.lensOn : ''}`}
        data-side={side}
        onClick={() => { setLens(on ? null : { side, span }); setLimits({}) }}
        title={side === 'back' ? `Concluded within the last ${span} days` : `Event within the next ${span} days`}
      >
        {label}
        <span className={styles.lensCount}>{count.toLocaleString()}</span>
      </button>
    )
  }

  return (
    /* `data-dense-view` tells AppShell this is a TABLE, not prose, so the
     * 1,180px reading measure does not apply. Without it the 11-column table
     * loses ~400px of columns behind a scrollbar on a wide desktop while the
     * screen sits half empty. See AppShell.module.css. */
    <div className={styles.stages} data-dense-view>
      {/* THE HUNT. Appears only when rows are ticked — the unit of work is the
          SELECTION, not the row, because fifty rows was fifty clicks. */}
      {selection && <HuntBar selection={selection} onOpen={onOpen} onAccepted={onAccepted} />}

      {/* THE LENS — rolling look-back / look-forward, cumulative, off effDate */}
      <div className={styles.lensBar}>
        <div className={styles.lensGroup}>
          <span className={styles.lensLabel}>Look back</span>
          {WINDOW_STEPS.map((n) => pill('back', n, `Last ${n}d`, counts[`back${n}`]))}
        </div>
        <div className={styles.lensGroup}>
          <span className={styles.lensLabel}>Look forward</span>
          {WINDOW_STEPS.map((n) => pill('fwd', n, `Next ${n}d`, counts[`next${n}`]))}
        </div>
        <div className={styles.lensGroup}>
          <button
            className={`${styles.lensPill} cc-lenspill ${!lens ? styles.lensOn : ''}`}
            onClick={() => { setLens(null); setLimits({}) }}
            title="Show the entire universe — nothing filtered out"
          >
            All records
            <span className={styles.lensCount}>{counts.total.toLocaleString()}</span>
          </button>
        </div>
      </div>

      {/* THE STAGE SUMMARY — EVERY stage, derived from the model.
          The action groups keep their prominence; the remaining stages get a
          smaller chip each rather than being folded into a sentence. The bar
          therefore partitions the universe, and says so with a real number. */}
      <div className={`${styles.stageBar} cc-stagebar`}>
        {tally.primary.map((s) => (
          <div key={s.key} className={styles.stageChip} style={{ '--stage': s.color }}>
            <span className={styles.stageCount}>{s.count.toLocaleString()}</span>
            <span className={styles.stageName}>{s.label}</span>
            <span className={`${styles.stageHint} cc-stagehint`}>{s.hint}</span>
          </div>
        ))}
      </div>
      <div className={`${styles.stageRest} cc-stagerest`}>
        {tally.rest.map((s) => (
          <div key={s.key} className={styles.restChip} style={{ '--stage': s.color }} title={s.hint}>
            <span className={styles.restCount}>{s.count.toLocaleString()}</span>
            <span className={styles.restName}>{s.label}</span>
          </div>
        ))}
        <span className={styles.restTally} data-ok={tally.reconciled ? '1' : '0'}>
          {tally.reconciled
            ? `= ${tally.accounted.toLocaleString()} — every record in view is in exactly one stage above`
            : `⚠ these stages account for ${tally.accounted.toLocaleString()} of ${tally.total.toLocaleString()} — ${Math.abs(tally.missing).toLocaleString()} unaccounted${tally.stray.length ? ` (unknown: ${tally.stray.map((s) => s.key).join(', ')})` : ''}`}
        </span>
      </div>

      <div className={styles.rowsBar}>
        <span>
          Showing <strong>{rendered.toLocaleString()}</strong> of{' '}
          <strong>{totalScoped.toLocaleString()}</strong> record{totalScoped === 1 ? '' : 's'}
          {lens ? ` in the ${lens.side === 'back' ? 'last' : 'next'} ${lens.span} days` : ' — every bucket below, nothing hidden'}
        </span>
        <span className={styles.rowsActions}>
          {!allRendered && (
            <button className={styles.linkBtn} onClick={expandAll}>
              Expand every bucket ({(totalScoped - rendered).toLocaleString()} more rows)
            </button>
          )}
          {rendered > PURSUIT_STAGES.length * PAGE && (
            <button className={styles.linkBtn} onClick={collapseAll}>Collapse to pages</button>
          )}
        </span>
      </div>

      {/* MOBILE FIRST: below 640px this exact table restyles into cards.
          The `cc-*` hooks are UNHASHED GLOBAL class names defined in
          `styles/cc-mobile.css` — see that file's header for why the mobile
          layer must not live in this component's CSS module. The DOM is
          untouched, so every <tr>, every cell, and the `tName` cell the
          champion-truth painter attaches to, survive verbatim. */}
      <div className={`${styles.tableWrap} cc-cards-wrap cc-scrollcue`}>
        <table className={`${styles.table} cc-cards`}>
          <thead>
            <tr>
              {/* Select-all governs every row in the scoped view, not the page. */}
              <SelectAllTh rows={scoped} selection={selection} label="rows in this view" />
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`${col.sortable ? styles.sortable : ''} ${sort.field === col.key ? styles.sortActive : ''}`}
                  onClick={col.sortable ? () => toggleSort(col.key) : undefined}
                >
                  {col.label}
                  {col.sortable && (
                    <span className={styles.sortArrow}>
                      {sort.field === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {PURSUIT_STAGES.map((stage) => {
              const list = sorted[stage.key]
              const limit = limitFor(stage.key)
              const shown = limit >= list.length ? list : list.slice(0, limit)
              return (
                <Fragment key={stage.key}>
                  <tr className={`${styles.groupBand} cc-band`} style={{ '--stage': stage.color }}>
                    <td colSpan={fullSpan}>
                      <span className={styles.groupDot} />
                      <span className={styles.groupName}>{stage.label}</span>
                      <span className={styles.groupCount}>{list.length.toLocaleString()}</span>
                      <span className={`${styles.groupHint} cc-bandhint`}>{stage.hint}</span>
                      {list.length > shown.length && (
                        <span className={styles.groupShowing}>
                          showing {shown.length.toLocaleString()} of {list.length.toLocaleString()}
                        </span>
                      )}
                    </td>
                  </tr>
                  {shown.map((c) => {
                    const sm = sportMeta(c.sport)
                    const cm = contentionMeta(c)
                    const days = daysToEvent(c, now)
                    const owner = ownerName(c)
                    return (
                      <tr key={c.id} className={styles.rowClickable} onClick={() => onOpen && onOpen(c)}>
                        <SelectCell item={c} selection={selection} />
                        {/* data-col / data-label are PRESENTATION HOOKS ONLY.
                            They let the card layout order and label each cell
                            from CSS without restructuring a single cell — which
                            is what keeps the champion-truth painter working. */}
                        <td className={styles.tName} data-col="name">
                          <span className={styles.sportAccent} style={{ background: sm.color }} title={sm.label} />
                          {c.name}
                          {/* Winner claims and references live once in the Champion column. */}
                        </td>
                        {!shape.pursuit && <td className={styles.tDim} data-col="sport" data-label="Sport"><span className={styles.sportDot} style={{ background: sm.color }} />{c.sport || '—'}</td>}
                        {shape.product && <td className={c.product ? styles.tDim : styles.tGap} data-col="product" data-label="Product">{c.product || '—'}</td>}
                        <td className={styles.tDim} data-col="level" data-label="Level">{c.level || <span className={styles.tGap}>—</span>}</td>
                        {!shape.pursuit && <td data-col="segment" data-label="Division">{c.segment ? <span className={styles.divChip}>{c.segment}</span> : <span className={styles.tGap}>—</span>}</td>}
                        {shape.state && (
                          <td className={c.state && c.state !== 'NATIONAL' ? styles.tDim : styles.tGap} data-col="state" data-label="State">
                            {c.state && c.state !== 'NATIONAL' ? c.state : '—'}
                          </td>
                        )}
                        {shape.open && (
                          <td
                            className={c.openDate ? styles.tDim : styles.tGap}
                            data-col="openDate"
                            data-label="Surfaced"
                            title={c.openDateIsProxy ? 'Scan date — when the engine surfaced this posting, not the issuer’s open date' : undefined}
                          >
                            {c.openDate ? formatDate(c.openDate) : '—'}
                          </td>
                        )}
                        <td className={c.effDate ? '' : styles.tGap} data-col="date" data-label={dateLabel}>
                          {c.effDate ? formatDate(c.effDate) : 'NO DATE'}{' '}<DateStamp c={c} compact />
                          {c.dateEstimated && <span className={styles.estBadge} title="Estimated from season + sport">est</span>}
                          {c.effDate && <span className={styles.rowCountdown}>{formatCountdownFor(c, days)}</span>}
                        </td>
                        <td data-col="status" data-label="Status"><span className={styles.statusChip} style={{ color: cm.color, borderColor: cm.color + '55', background: cm.color + '12' }}>{c.sourceType && c.stageLabel ? c.stageLabel : cm.label}</span></td>
                        {/* THE CHAMPION CELL. Winner, trust, issue flags and the
                            source URL, all computed from the row itself — no
                            second request, no panel, nothing to click first.
                            `compact` keeps it to one dense block so a 427-row
                            table stays scannable; the full facts are in the
                            dossier. */}
                        {!shape.pursuit && (
                          <td className={styles.tChampion} data-col="champion" data-label="Champion">
                            {/* "Full dossier" must open the DOSSIER — the
                                Champion-to-Close panel with the brand kit, ring
                                surfaces, price and the outreach sequence. It was
                                wired to `onOpen`, which opens the record drawer:
                                a different panel showing the record's fields.
                                The button's own tooltip promised the first and
                                delivered the second, which is the kind of small
                                lie that teaches people to stop trusting labels.
                                Falls back to the drawer only where the dossier
                                action does not exist (read-only roles). */}
                            <ChampionCell
                              record={crownedView(c, justCrowned)}
                              onDossier={actions?.onCampaign || actions?.onCreateArt || onOpen}
                              compact
                              /* Same optimistic repaint the bulk bar gets. Without
                                 it the Champion column keeps reading "no champion"
                                 next to one the rep just recorded. */
                              onAccepted={onAccepted}
                            />
                          </td>
                        )}
                        {!shape.pursuit && <td className={styles.tArtwork} data-col="artwork" data-label="Artwork" onClick={event=>event.stopPropagation()}><ChampionshipArtworkCell record={c}/></td>}
                        {!shape.pursuit && <td className={c.incumbent ? styles.tDim : styles.tGap} data-col="incumbent" data-label="Incumbent">{c.incumbent || 'unknown'}</td>}
                        <td className={owner ? styles.tDim : styles.tGap} data-col="owner" data-label="Assigned rep">
                          {owner || 'Unassigned'}
                          <SchoolTeamCoverage record={c}/>
                          {/* The trade on this record, inline (2026-10-19). Nothing when there is none. */}
                          <TradeChip recordId={c.id} />
                        </td>
                        <td className={styles.tActions} data-col="cta" onClick={(e) => e.stopPropagation()}>
                          <span className={styles.quickBar}>
                            {canWrite && (
                              <>
                                {!splitParentSummary(c) && <><button className={styles.quick} title="Create Art — assemble the ring proof" onClick={() => actions.onCreateArt?.(c)}>Art</button>
                                <button className={styles.quick} title="Quote — open the pricing engine" onClick={() => actions.onQuote?.(c)}>Quote</button>
                                <button className={styles.quick} title="Email campaign — draft the closing sequence" onClick={() => actions.onCampaign?.(c)}>Email</button></>}
                                <button className={`${styles.quick} ${styles.quickWin}`} title="Review winner evidence; no crown is written" onClick={() => actions.onSubmitWin?.(c)}>{splitParentSummary(c) ? 'Review split results' : 'Review winner'}</button>
                              </>
                            )}
                            {/* The official channel to participate, right on
                                the row. Renders only where the record carries
                                one — a championship row is untouched. */}
                            <SubmitLink record={c} />
                            <button className={styles.ctaOpen} onClick={() => onOpen && onOpen(c)}>Open ›</button>
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                  {list.length > shown.length && (
                    <tr>
                      <td colSpan={fullSpan} className={`${styles.more} cc-more`}>
                        <button className={styles.moreBtn} onClick={() => bump(stage.key, limit + PAGE * 5)}>
                          Show {Math.min(PAGE * 5, list.length - shown.length).toLocaleString()} more
                        </button>
                        <button className={styles.moreBtn} onClick={() => bump(stage.key, list.length)}>
                          Show all {list.length.toLocaleString()} in {stage.label}
                        </button>
                        <span className={styles.moreNote}>
                          {(list.length - shown.length).toLocaleString()} not yet rendered — reachable, not hidden
                        </span>
                      </td>
                    </tr>
                  )}
                  {list.length > PAGE && shown.length === list.length && (
                    <tr>
                      <td colSpan={fullSpan} className={`${styles.more} cc-more`}>
                        <button className={styles.moreBtn} onClick={() => bump(stage.key, PAGE)}>
                          Collapse {stage.label} to the first {PAGE.toLocaleString()}
                        </button>
                      </td>
                    </tr>
                  )}
                  {list.length === 0 && (
                    <tr><td colSpan={fullSpan} className={styles.groupEmpty}>None in {stage.label}.</td></tr>
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* THE RECONCILIATION IS AN ASSERTION, NOT A CLAIM.
       *
       * This used to end with the flat sentence "every bucket is listed above,
       * including Earlier, Later and No date" — printed unconditionally, so it
       * read identically whether it was true or not. It was the sentence an
       * auditor was reading at the moment they concluded 203 records were
       * missing, which is the worst possible time for a claim to be unchecked.
       *
       * It now states the checked property and names the count that proves it,
       * and when the check fails it says so instead of reassuring. */
      }
      <div className={styles.tallyFoot} data-reconciled={tally.reconciled ? '1' : '0'}>
        {rendered.toLocaleString()} rows rendered · {totalScoped.toLocaleString()}
        {lens ? ' in the selected window' : ' in view'}
        {lens ? '' : ` · ${counts.total.toLocaleString()} loaded`}
        {' — '}
        {tally.reconciled ? (
          <>
            checked: all {tally.rows.length} stages above sum to{' '}
            {tally.accounted.toLocaleString()}, the whole of this view. Nothing is hidden;
            un-rendered rows are paged, and every group states its own full count.
          </>
        ) : (
          <strong className={styles.tallyBad}>
            RECONCILIATION FAILED — the {tally.rows.length} stages above sum to{' '}
            {tally.accounted.toLocaleString()}, but this view holds{' '}
            {tally.total.toLocaleString()}. {Math.abs(tally.missing).toLocaleString()} record
            {Math.abs(tally.missing) === 1 ? ' is' : 's are'} unaccounted for
            {tally.stray.length ? ` (unknown stage keys: ${tally.stray.map((s) => `${s.key}×${s.count}`).join(', ')})` : ''}
            . Do not work this board until it is fixed — use the Worklist.
          </strong>
        )}
      </div>
    </div>
  )
}
